// Users & roles (S-3, admins): every user of the Hub with their role, 2-step and state; Add user (a role and a one-time
// password shown once: they choose their own at first sign-in); change role, disable / enable, reset password, reset
// 2-step. Never on yourself (My profile), never leaving the Hub without an active admin. On the fixtures (npm test)
// the read-only table of the dev users.
import { useEffect, useState } from 'react'
import { UserPlus } from 'lucide-react'
import { Badge, ConfirmDialog, Drawer, KeysShownOnce, PageHeader, formatDateTime, notify, type BadgeTone } from '@/kit/ui'
import { apiMode } from '@/lib/hubApi'
import { ROLES, users as usersApi, type AdminUser, type Role, type UserMade } from '@/lib/usersApi'
import { PERMISSIONS } from '@/store/devStore'
import { useHubStore } from '@/store/hubStore'

const ROLE_TONE: Record<Role, BadgeTone> = { admin: 'primary', editor: 'info', reviewer: 'violet', analyst: 'gray', mis_uploader: 'warning' }
const roleLabel = (r: Role) => ROLES.find((x) => x.id === r)?.label ?? r

type Ask = { kind: 'role'; user: AdminUser; role: Role } | { kind: 'disable' | 'enable' | 'password' | '2step'; user: AdminUser }

export function UsersScreen() {
  const fixtures = useHubStore((s) => s.users)
  const [list, setList] = useState<AdminUser[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [adding, setAdding] = useState(false)
  const [asking, setAsking] = useState<Ask | null>(null)
  const [busy, setBusy] = useState(false)
  const [shown, setShown] = useState<{ title: string; made: UserMade } | null>(null)
  useEffect(() => { if (apiMode()) usersApi.list().then(setList, (e: Error) => setError(e.message)) }, [])
  const put = (u: AdminUser) => setList((l) => (l ?? []).map((x) => (x.id === u.id ? u : x)))

  const act = async (a: Ask) => {
    setBusy(true)
    try {
      if (a.kind === 'role') { put(await usersApi.update(a.user.id, { role: a.role })); notify.success(`${a.user.name} is now ${roleLabel(a.role)}`, 'They sign in again with the new rights.') }
      if (a.kind === 'disable' || a.kind === 'enable') { put(await usersApi.update(a.user.id, { active: a.kind === 'enable' })); notify.success(`${a.user.name} ${a.kind === 'enable' ? 'enabled' : 'disabled'}`) }
      if (a.kind === 'password') { const r = await usersApi.resetPassword(a.user.id); put(r.user); setShown({ title: `${a.user.name}: new one-time password`, made: r }) }
      if (a.kind === '2step') { put(await usersApi.resetTwoStep(a.user.id)); notify.success(`${a.user.name}: 2-step reset`, 'They set it up again at their next sign-in.') }
    } catch (e) {
      notify.error('Not done', (e as Error).message)
    } finally {
      setBusy(false)
      setAsking(null)
    }
  }

  if (!apiMode()) {
    return (
      <>
        <PageHeader title="Users & roles" subtitle="admin · editor · reviewer · analyst. Adding users, passwords and 2-step need the Hub API." />
        <div className="card-flush overflow-x-auto">
          <table className="table">
            <thead><tr><th>User</th><th>Role</th><th>2-step</th><th>Last sign-in</th></tr></thead>
            <tbody>{fixtures.map((u) => <tr key={u.id}><td>{u.name} · <span className="text-muted">{u.email}</span></td><td><Badge tone={ROLE_TONE[u.role]}>{u.role}</Badge></td><td>{u.twoStep ? 'on' : 'off'}</td><td className="mono">{formatDateTime(u.lastSignInAt)}</td></tr>)}</tbody>
          </table>
        </div>
        <RoleTable />
      </>
    )
  }

  const confirmText = (a: Ask) => ({ role: `Make ${roleLabel((a as { role: Role }).role ?? 'analyst')}`, disable: 'Disable', enable: 'Enable', password: 'Reset the password', '2step': 'Reset 2-step' })[a.kind]
  const message = (a: Ask) => ({
    role: `${a.user.name} becomes ${roleLabel((a as { role: Role }).role ?? 'analyst')}: ${ROLES.find((r) => r.id === (a as { role: Role }).role)?.can}. They are signed out and sign in again.`,
    disable: `${a.user.name} is signed out and cannot sign in until enabled again. Their History stays.`,
    enable: `${a.user.name} can sign in again with their password.`,
    password: `${a.user.name} gets a new one-time password (shown once here; give it to them yourself). Their old password stops and they are signed out.`,
    '2step': `${a.user.name}'s 2-step is removed (for a lost phone). They are signed out and set it up again at their next sign-in.`,
  })[a.kind]

  return (
    <>
      <PageHeader title="Users & roles" subtitle="Who may use the Hub, with which role. Admins only; every change writes History." actions={<button type="button" className="btn btn-primary" onClick={() => setAdding(true)} data-testid="add-user"><UserPlus size={15} /> Add user</button>} />
      {error && <p className="field-error">{error}</p>}
      {shown && (
        <div className="mb-3 max-w-2xl" data-testid="password-shown-once">
          <KeysShownOnce title={`${shown.title} · shown once`} secrets={{ [`${shown.made.user.email}`]: shown.made.oneTimePassword }} note="Give it to them yourself (not in a group chat). They sign in at the Hub with it and choose their own password at once; it works only for that first sign-in." />
          <button type="button" className="btn btn-secondary btn-sm mt-1" onClick={() => setShown(null)}>I have given it to them</button>
        </div>
      )}
      {!list && !error && <p className="text-2xs text-muted">Loading…</p>}
      {list && (
        <div className="card-flush overflow-x-auto">
          <table className="table" data-testid="users-table">
            <thead><tr><th>User</th><th>Role</th><th>2-step</th><th>State</th><th>Last sign-in</th><th /></tr></thead>
            <tbody>
              {list.map((u) => (
                <tr key={u.id} data-testid={`user-${u.email}`} className={u.status === 'disabled' ? 'opacity-60' : ''}>
                  <td>{u.name} {u.you && <Badge tone="success">you</Badge>}<span className="block text-2xs text-muted">{u.email}</span></td>
                  <td>
                    {u.you || u.status === 'disabled' ? <Badge tone={ROLE_TONE[u.role]}>{roleLabel(u.role)}</Badge> : (
                      <select className="input !min-h-7 w-32 py-0.5 text-2xs" aria-label={`Role of ${u.name}`} value={u.role} onChange={(e) => setAsking({ kind: 'role', user: u, role: e.target.value as Role })}>
                        {ROLES.map((r) => <option key={r.id} value={r.id}>{r.label}</option>)}
                      </select>
                    )}
                  </td>
                  <td className="text-2xs">{u.twoStep ? <>on <span className="text-muted">· {u.recoveryCodesLeft} codes</span></> : <span className="text-amber-600 dark:text-amber-300">off</span>}</td>
                  <td className="text-2xs">
                    {u.status === 'disabled' ? <Badge tone="gray">disabled</Badge> : u.locked ? <Badge tone="danger">locked</Badge> : u.mustChangePassword ? <Badge tone="warning">first sign-in pending</Badge> : <Badge tone="success">active</Badge>}
                  </td>
                  <td className="text-2xs">{u.lastSignInAt ? formatDateTime(u.lastSignInAt) : 'never'}</td>
                  <td className="whitespace-nowrap text-right">
                    {!u.you && (
                      <div className="flex justify-end gap-1">
                        {u.status === 'active' && <button type="button" className="btn btn-secondary btn-sm" onClick={() => setAsking({ kind: 'password', user: u })} data-testid={`reset-password-${u.email}`}>Reset password</button>}
                        {u.status === 'active' && u.twoStep && <button type="button" className="btn btn-secondary btn-sm" onClick={() => setAsking({ kind: '2step', user: u })}>Reset 2-step</button>}
                        <button type="button" className="btn btn-secondary btn-sm" onClick={() => setAsking({ kind: u.status === 'active' ? 'disable' : 'enable', user: u })} data-testid={`toggle-${u.email}`}>{u.status === 'active' ? 'Disable' : 'Enable'}</button>
                      </div>
                    )}
                    {u.you && <span className="text-2xs text-muted">your account: My profile</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <RoleTable />
      <AddUser isOpen={adding} onClose={() => setAdding(false)} onAdded={(r) => { setList((l) => [...(l ?? []), r.user]); setShown({ title: `${r.user.name} added`, made: r }); setAdding(false) }} />
      <ConfirmDialog isOpen={asking !== null} onClose={() => setAsking(null)} busy={busy} type={asking?.kind === 'enable' || asking?.kind === 'role' ? 'warning' : 'danger'}
        title={asking ? `${confirmText(asking)}?` : ''} message={asking ? message(asking) : ''} confirmText={asking ? confirmText(asking) : ''} onConfirm={() => { if (asking) void act(asking) }} />
    </>
  )
}

function RoleTable() {
  return (
    <section className="card mt-3 overflow-x-auto">
      <h2 className="section-title">The roles</h2>
      <table className="table" data-testid="role-table">
        <thead><tr><th>Role</th><th>May</th><th>Permissions</th></tr></thead>
        <tbody>{ROLES.map((r) => <tr key={r.id}><td><Badge tone={ROLE_TONE[r.id]}>{r.label}</Badge></td><td className="text-xs">{r.can}</td><td className="mono text-2xs text-muted">{PERMISSIONS[r.id].join(' ')}</td></tr>)}</tbody>
      </table>
    </section>
  )
}

function AddUser({ isOpen, onClose, onAdded }: { isOpen: boolean; onClose: () => void; onAdded: (r: UserMade) => void }) {
  const [email, setEmail] = useState('')
  const [name, setName] = useState('')
  const [role, setRole] = useState<Role>('analyst')
  const [busy, setBusy] = useState(false)
  useEffect(() => { if (isOpen) { setEmail(''); setName(''); setRole('analyst') } }, [isOpen])
  const okEmail = /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email.trim())
  const add = async () => {
    setBusy(true)
    try { onAdded(await usersApi.add({ email: email.trim(), name: name.trim(), role })) } catch (e) { notify.error('Not added', (e as Error).message) } finally { setBusy(false) }
  }
  return (
    <Drawer isOpen={isOpen} onClose={onClose} title="Add a user" subtitle="They get a one-time password from you and choose their own at first sign-in" size="md"
      footer={<div className="flex justify-end gap-2"><button type="button" className="btn btn-ghost" onClick={onClose}>Cancel</button><button type="button" className="btn btn-primary" disabled={busy || !okEmail || !name.trim()} onClick={() => void add()} data-testid="add-user-save">Add user</button></div>}>
      <div className="space-y-3">
        <div><label className="label" htmlFor="nu-name">Name</label><input id="nu-name" className="input" maxLength={80} value={name} onChange={(e) => setName(e.target.value)} /></div>
        <div><label className="label" htmlFor="nu-email">Email (their sign-in)</label><input id="nu-email" className="input" type="email" maxLength={254} value={email} onChange={(e) => setEmail(e.target.value)} />{email && !okEmail && <p className="field-error">An email like name@firstfundfincorp.com</p>}</div>
        <fieldset>
          <legend className="label">Role</legend>
          {ROLES.map((r) => (
            <label key={r.id} className="flex items-start gap-2 py-1 text-xs">
              <input type="radio" name="nu-role" className="mt-0.5" checked={role === r.id} onChange={() => setRole(r.id)} />
              <span><b>{r.label}</b><span className="block text-2xs text-muted">{r.can}</span></span>
            </label>
          ))}
        </fieldset>
        <p className="text-2xs text-muted">Give the least role that lets them do their work; you can change it later. Where the Hub requires 2-step (the server), they set it up at their first sign-in.</p>
      </div>
    </Drawer>
  )
}
